import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  getFirestore,
  updateDoc,
  increment,
  arrayUnion,
  arrayRemove,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
} from "firebase/firestore";
//import { db } from "../../lib/firebase";
//import { getFirestore} from "firebase/firestore";

import { app } from "../../lib/firebase";

const db = getFirestore(app);
// "../lib/firebase";
interface User {
  id: string;
  username: string;
  createdAt: Date;
}

interface Post {
  id: string;
  content: string;
  userId: string;
  likedBy: string[];
  commentIds: string[];
  createdAt: Date;
  imageUrl?: string;
  videoUrl?: string;
}

interface Comment {
  id: string;
  text: string;
  userId: string;
  postId: string;
  createdAt: Date;
}

interface FirestorePost extends Omit<Post, "createdAt"> {
  createdAt: Timestamp;
}

interface FirestoreComment extends Omit<Comment, "createdAt"> {
  createdAt: Timestamp;
}

interface FirestoreUser extends Omit<User, "createdAt"> {
  createdAt: Timestamp;
}

// Function to create a new post
const createPost = async (
  content: string,
  userId: string,
  imageUrl?: string,
  videoUrl?: string
) => {
  try {
    const postsCollection = collection(db, "posts");
    await addDoc(postsCollection, {
      content,
      userId,
      likedBy: [],
      imageUrl: imageUrl || null,
      videoUrl: videoUrl || null,
      createdAt: serverTimestamp(),
    });
    console.log("Post created successfully!");
  } catch (error) {
    console.error("Error creating post:", error);
    throw error;
  }
};

// Function to get recent posts with pagination
const getPosts = async (
  limitCount: number = 10,
  lastPost?: QueryDocumentSnapshot<DocumentData>
): Promise<Post[]> => {
  try {
    let postsQuery;
    if (lastPost) {
      postsQuery = query(
        collection(db, "posts"),
        orderBy("createdAt", "desc"),
        limit(limitCount),
        where("createdAt", "<", lastPost.data().createdAt)
      );
    } else {
      postsQuery = query(
        collection(db, "posts"),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(postsQuery);
    const posts: Post[] = [];

    // Get all posts first
    const postDocs = querySnapshot.docs.map((doc) => {
      const data = doc.data() as FirestorePost;
      return {
        ...data,
        id: doc.id,
        commentIds: [], // Initialize empty array for comment IDs
      };
    });

    // Get comments for all posts in a single query
    const commentsQuery = query(
      collection(db, "comments"),
      where(
        "postId",
        "in",
        postDocs.map((post) => post.id)
      ),
      orderBy("createdAt", "desc")
    );

    const commentsSnapshot = await getDocs(commentsQuery);
    const commentIdsByPost = new Map<string, string[]>();

    commentsSnapshot.forEach((doc) => {
      const data = doc.data() as FirestoreComment;
      if (!commentIdsByPost.has(data.postId)) {
        commentIdsByPost.set(data.postId, []);
      }
      commentIdsByPost.get(data.postId)?.push(doc.id);
    });

    // Combine posts with their comment IDs
    postDocs.forEach((doc) => {
      posts.push({
        id: doc.id,
        content: doc.content,
        userId: doc.userId,
        likedBy: doc.likedBy,
        imageUrl: doc.imageUrl,
        videoUrl: doc.videoUrl,
        createdAt: doc.createdAt.toDate(),
        commentIds: commentIdsByPost.get(doc.id) || [],
      });
    });

    return posts;
  } catch (error) {
    console.error("Error getting posts:", error);
    throw error;
  }
};

// Function to create a new comment
const createComment = async (postId: string, userId: string, text: string) => {
  try {
    const commentsCollection = collection(db, "comments");
    const commentDoc = await addDoc(commentsCollection, {
      postId,
      userId,
      text,
      createdAt: serverTimestamp(),
    });

    // Add the comment ID to the post's commentIds array
    const postRef = doc(db, "posts", postId);
    await updateDoc(postRef, {
      commentIds: arrayUnion(commentDoc.id),
    });

    console.log("Comment added successfully!");
    return commentDoc.id;
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

// Function to get comments by their IDs
const getCommentsByIds = async (commentIds: string[]): Promise<Comment[]> => {
  try {
    if (commentIds.length === 0) return [];

    const commentsQuery = query(
      collection(db, "comments"),
      where("__name__", "in", commentIds)
    );

    const querySnapshot = await getDocs(commentsQuery);
    const comments: Comment[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data() as FirestoreComment;
      comments.push({
        id: doc.id,
        text: data.text,
        userId: data.userId,
        postId: data.postId,
        createdAt: data.createdAt.toDate(),
      });
    });

    return comments;
  } catch (error) {
    console.error("Error getting comments:", error);
    throw error;
  }
};

// Function to like a post
const likePost = async (postId: string, userId: string) => {
  try {
    const postRef = doc(db, "posts", postId);
    await updateDoc(postRef, {
      likedBy: arrayUnion(userId),
    });
    console.log("Post liked!");
  } catch (error) {
    console.error("Error liking post:", error);
    throw error;
  }
};

// Function to unlike a post
const unlikePost = async (postId: string, userId: string) => {
  try {
    const postRef = doc(db, "posts", postId);
    await updateDoc(postRef, {
      likedBy: arrayRemove(userId),
    });
    console.log("Post unliked!");
  } catch (error) {
    console.error("Error unliking post:", error);
    throw error;
  }
};

// Function to get user profile
const getUser = async (userId: string): Promise<User | null> => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const data = userDoc.data() as FirestoreUser;
      return {
        id: userDoc.id,
        username: data.username,
        createdAt: data.createdAt.toDate(),
      };
    }
    return null;
  } catch (error) {
    console.error("Error getting user:", error);
    throw error;
  }
};

// Function to update user profile
const updateUser = async (
  userId: string,
  data: Partial<Omit<User, "id" | "createdAt">>
) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
    console.log("User profile updated!");
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

export {
  createPost,
  getPosts,
  createComment,
  getCommentsByIds,
  likePost,
  unlikePost,
  getUser,
  updateUser,
  type User,
  type Post,
  type Comment,
};
